import type { ConnectionState, StateDetail } from '../net/listenerClient';

type Tone = 'live' | 'wait' | 'bad' | 'idle';

function describe(
  state: ConnectionState,
  detail: StateDetail,
  running: boolean,
): { tone: Tone; label: string } {
  switch (state) {
    case 'live':
      return { tone: 'live', label: 'Live' };
    case 'waiting':
      return { tone: 'wait', label: 'Waiting for the stream' };
    case 'connecting':
      return { tone: 'wait', label: 'Connecting' };
    case 'reconnecting': {
      const seconds = detail.retryInMs ? Math.max(1, Math.round(detail.retryInMs / 1000)) : null;
      return {
        tone: 'wait',
        label: seconds ? `Reconnecting in ${seconds}s` : 'Reconnecting',
      };
    }
    case 'failed':
      return { tone: 'bad', label: detail.reason ? `Stopped: ${detail.reason}` : 'Stopped' };
    case 'idle':
    default:
      return { tone: 'idle', label: running ? 'Idle' : 'Not connected' };
  }
}

/**
 * The connection state is the one thing a listener always needs to know, so it is
 * announced politely rather than left as a silent colour change.
 */
export function ConnectionBadge({
  state,
  detail,
  running,
}: {
  state: ConnectionState;
  detail: StateDetail;
  running: boolean;
}) {
  const { tone, label } = describe(state, detail, running);
  return (
    <p className="badge" data-tone={tone} role="status" aria-live="polite">
      <span className="dot" aria-hidden="true" />
      {label}
    </p>
  );
}
